class MapWidget extends BaseWidget<MapResultVM, MapConfig> {
    override widgetType = WidgetType.Map;

    readonly notFoundColumn: string = "notFound";

    constructor(container: any, tab: WorkbookTab, $reportContent, dashboardWidget: DashboardWidget) {
        super(container, tab, $reportContent, dashboardWidget);

        this.disableDrilldown = true;
    }

    setConfig(config) {
        this.itemConfig = new MapConfig(config);
    }

    setResult(result) {
        this.result = new MapResultVM(result);

        this.result.dataSource = this.getDataSource();
        let paletteColorGroups = this.getPaletteColorGroups();
        let centerTimeoutId = null;
        let zoomTimeoutId = null;

        let self = this;
        this.result.mapSettings = {
            width: "100%",
            height: "100%",
            zoomFactor: this.itemConfig.zoomFactor() as any,
            center: this.itemConfig.center(),
            zoomingEnabled: this.result && workbook.workbookConfig.isPrinting ? false : this.itemConfig.zoomingEnabled as any,
            panningEnabled: this.result && workbook.workbookConfig.isPrinting ? false : this.itemConfig.panningEnabled as any,
            tooltip: {
                enabled: true,
                border: {
                    visible: false,
                },
                customizeTooltip(arg) {
                    if (arg.attribute("apply")) {
                        const color = arg.attribute('backgroundColor');
                        const fontColor = arg.attribute('fontColor');
                        const field = arg.attribute('field');
                        const metric = arg.attribute('metricValue');
                        return {
                            text: `${field}: ${self.formatValue(metric)}`,
                            color: color ? color : undefined,
                            fontColor: fontColor ? fontColor : undefined
                        };
                    }

                    let text = arg.attribute(self.itemConfig.attributeName());
                    if (text) {
                        text = text.replace(/\u0000/g, '');
                    }
                    return {
                        text: text
                    };
                },
            },
            layers: {
                name: "areas",
                dataSource: undefined,
                palette: paletteColorGroups.palette,
                colorGroups: paletteColorGroups.colorGroups,
                colorGroupingField: 'metricValue',
                customize(elements) {
                    $.each(elements, (_, element) => {
                        let key = element.attribute(self.itemConfig.attributeName());
                        if (typeof key === "string" && key) {
                            key = key.toLowerCase().trimEnd().replace(/\0/g, '');
                        }

                        const row = self.result.dataSource[key];
                        if (row) {
                            self.result.dataSource[key][self.notFoundColumn] = false;

                            let color = "#337ab7";
                            let fontColor = "white";
                            if (self.itemConfig.colorization() === MapColorizationEnum.Range && self.itemConfig.formattingRule()) {
                                const scale = FormattingRuleService.getFormattingRuleScale(FormattingRuleService.getFormattingRuleById(self.itemConfig.formattingRule()), row[self.result.metric.queryName], self.result.metric.metricFormat === FieldFormatEnum.Percentage);
                                if (scale && scale.color) {
                                    color = scale.color;
                                }

                                if (scale && scale.backgroundColor && !color) {
                                    color = scale.backgroundColor;
                                }

                                if (scale && scale.fontColor) {
                                    fontColor = scale.fontColor;
                                }

                                element.attribute('backgroundColor', color);
                                element.attribute('fontColor', fontColor);
                            }

                            element.attribute("apply", true);
                            element.attribute('field', row[self.result.field.queryName]);
                            element.attribute('metricValue', row[self.result.metric.queryName]);
                        }
                    });

                    if (self.result.dataSource) {
                        const missingRecords = Object.values(self.result.dataSource).filter(x => x[self.notFoundColumn]);
                        const fieldName = getFieldDisplayName(self.itemConfig.field(), self.itemConfig.fieldAutoGrouping());
                        self.result.warningVisible(workbook.workbookConfig.isPrinting ? false : missingRecords.length > 0);
                        self.result.warningText(!self.result.warningVisible() ? "" : `${fieldName} not found: ${missingRecords.map((x) => x[self.result.field.queryName]).join(', ')}`);
                    }
                },
            },
            legends: [{
                title: this.itemConfig.legend.title(),
                source: { layer: 'areas', grouping: 'color' },
                customizeText(arg) {
                    let text;
                    let end = self.formatValue(arg.end, true);
                    let start = self.formatValue(arg.start, true);
                    if (arg.index === 0) {
                        text = `< ${end}`;
                    } else if (arg.index === self.result.legendLength) {
                        text = `> ${start}`;
                    } else {
                        text = `${start} to ${end}`;
                    }
                    return text;
                },
                visible: this.result?.dataTable && ((this.itemConfig.colorization() === MapColorizationEnum.Range && this.itemConfig.formattingRule()) || this.itemConfig.colorization() === MapColorizationEnum.Gradient) && this.itemConfig.legend.showLegend()
            }],
            onInitialized: function(e) {
                self.result.mapComponent = e.component;
            },
            onCenterChanged: function (e) {
                if (self.itemConfig.zoomingEnabled()) {
                    if (centerTimeoutId) {
                        clearTimeout(centerTimeoutId);
                    }

                    centerTimeoutId = setTimeout(function () {
                        self.itemConfig.center(e.center);
                    }, 500);
                }
                else {
                    self.itemConfig.center(e.center);
                }
            },

            onZoomFactorChanged: function (e) {
                if (self.itemConfig.panningEnabled()) {
                    self.itemConfig.zoomFactor(e.zoomFactor);
                }
                else {
                    if (zoomTimeoutId) {
                        clearTimeout(zoomTimeoutId);
                    }

                    zoomTimeoutId = setTimeout(function () {
                        self.itemConfig.zoomFactor(e.zoomFactor);
                    }, 500);
                }
            }
        };
    }

    setupSettings() {
        let self = this;

        this.createSettingItem(this.itemConfig.source, "source", UpdateSettingMode.Custom, () => {
            if (self.result.dataSource) {
                Object.values(self.result.dataSource).forEach(function (r: any) {
                    r[self.notFoundColumn] = true;
                });

                self.result.warningText("");
                self.result.warningVisible(false);
            }

            self.getDataSourceMap().then(function (dataSourceMap) {
                if (dataSourceMap) {
                    self.result.mapComponent.option("layers.dataSource", dataSourceMap);
                }
            })
        });

        this.createSettingItem(this.itemConfig.attributeName, "attributeName", UpdateSettingMode.Custom, () => {
            self.result.mapComponent.beginUpdate();
            let dataSourceMap = self.result.mapComponent.option("layers.dataSource");
            self.result.mapComponent.option("layers.dataSource", undefined);
            self.result.mapComponent.option("layers.dataSource", dataSourceMap);
            self.result.mapComponent.endUpdate();
        });

        let updateLegend = function () {
            //Hack to change the title
            let legendSettings = self.result.mapComponent.option("legends");
            self.result.mapComponent.option("legends", undefined);
            legendSettings[0].visible = self.result?.dataTable && ((self.itemConfig.colorization() === MapColorizationEnum.Range && self.itemConfig.formattingRule()) || self.itemConfig.colorization() === MapColorizationEnum.Gradient) && self.itemConfig.legend.showLegend() ? true : false;
            self.result.mapComponent.option("legends", legendSettings);
        }
        this.createSettingItem(this.itemConfig.legend.showLegend, "legend.showLegend", UpdateSettingMode.Custom, updateLegend);
        this.createSettingItem(this.itemConfig.legend.format, "legend.format", UpdateSettingMode.Custom, updateLegend);
        this.createSettingItem(this.itemConfig.legend.decimalPlaces, "legend.decimalPlaces", UpdateSettingMode.Custom, updateLegend);
        this.createSettingItem(this.itemConfig.legend.unitFormat, "legend.unitFormat", UpdateSettingMode.Custom, updateLegend);

        this.createSettingItem(this.itemConfig.legend.title, "legend.title", UpdateSettingMode.Custom, () => {
            //Hack to change the title
            let legendSettings = self.result.mapComponent.option("legends");
            self.result.mapComponent.option("legends", undefined);
            legendSettings[0].title = self.itemConfig.legend.title();
            self.result.mapComponent.option("legends", legendSettings);
        });

        let update = function () {
            let paletteColorGroups = self.getPaletteColorGroups();
            self.result.mapComponent.beginUpdate();
            self.result.mapComponent.option("layers.palette", paletteColorGroups.palette);
            self.result.mapComponent.option("layers.colorGroups", paletteColorGroups.colorGroups);

            //Force reload data
            let dataSourceMap = self.result.mapComponent.option("layers.dataSource");
            self.result.mapComponent.option("layers.dataSource", undefined);
            self.result.mapComponent.option("layers.dataSource", dataSourceMap);

            self.result.mapComponent.endUpdate();

            //Hack to change the title
            let legendSettings = self.result.mapComponent.option("legends");
            self.result.mapComponent.option("legends", undefined);
            legendSettings[0].visible = self.result?.dataTable && ((self.itemConfig.colorization() === MapColorizationEnum.Range && self.itemConfig.formattingRule()) || self.itemConfig.colorization() === MapColorizationEnum.Gradient) && self.itemConfig.legend.showLegend() ? true : false;
            self.result.mapComponent.option("legends", legendSettings);
        };
        this.createSettingItem(this.itemConfig.formattingRule, "formattingRule", UpdateSettingMode.Custom, update);
        this.createSettingItem(this.itemConfig.colorization, "colorization", UpdateSettingMode.Custom, update);
        this.createSettingItem(this.itemConfig.startColor, "startColor", UpdateSettingMode.Custom, update);
        this.createSettingItem(this.itemConfig.endColor, "endColor", UpdateSettingMode.Custom, update);
        this.createSettingItem(this.itemConfig.gradientRanges, "gradientRanges", UpdateSettingMode.Custom, update);

        this.createSettingItem(this.itemConfig.metric, "metric", UpdateSettingMode.Reload);
        this.createSettingItem(this.itemConfig.field, "field", UpdateSettingMode.Reload);

        this.createSettingItem(this.itemConfig.zoomingEnabled, "zoomingEnabled", UpdateSettingMode.Automatic);
        this.createSettingItem(this.itemConfig.panningEnabled, "panningEnabled", UpdateSettingMode.Automatic);
        this.createSettingItem(this.itemConfig.zoomFactor, "zoomFactor", UpdateSettingMode.Automatic);
        this.createSettingItem(this.itemConfig.center, "center", UpdateSettingMode.Automatic);
    }

    repaint() {

    }

    runSetup() {

    }

    setup() {
        this.getDataSourceMap().then(function (this: MapWidget, dataSourceMap) {
            if (dataSourceMap) {
                this.result.mapComponent.option("layers.dataSource", dataSourceMap);
            }
        }.bind(this))
    }

    isCustomMap(source: string) {
        switch (source) {
            case MapDataSourceEnum.Africa:
            case MapDataSourceEnum.Canada:
            case MapDataSourceEnum.Europe:
            case MapDataSourceEnum.USA:
            case MapDataSourceEnum.World:
            case MapDataSourceEnum.USACities:
            case MapDataSourceEnum.IrelandCounties:
                return false;
            default:
                return true;
        }
    }

    private async getDataSourceMap() {
        var self = this;
        //let source = vm.mapSelectOptions.items.find(x => x.id === this.itemConfig.source());
        //if (!source) {
        //    this.itemConfig.source(MapDataSourceEnum.World);
        //    return null;
        //}

        function get() {
            return new Promise((resolve) => {
                self.result.fieldLabel("Source");
                switch (self.itemConfig.source()) {
                    case MapDataSourceEnum.Africa:
                        resolve((DevExpress.viz.map as any).sources.africa);

                        self.result.attributeNameItems(['name']);
                        self.result.fieldLabel("State");
                        break;
                    case MapDataSourceEnum.Canada:
                        resolve((DevExpress.viz.map as any).sources.canada);

                        self.result.attributeNameItems(['name']);
                        self.result.fieldLabel("State");
                        break;
                    case MapDataSourceEnum.Europe:
                        resolve((DevExpress.viz.map as any).sources.europe);

                        self.result.attributeNameItems(['name']);
                        self.result.fieldLabel("Country");
                        break;
                    case MapDataSourceEnum.USA:
                        resolve((DevExpress.viz.map as any).sources.usa);

                        self.result.attributeNameItems(['name']);
                        self.result.fieldLabel("State");
                        break;
                    case MapDataSourceEnum.World:
                        resolve((DevExpress.viz.map as any).sources.world);

                        self.result.attributeNameItems(['name']);
                        self.result.fieldLabel("Country");
                        break;
                    default:
                        {
                            (DevExpress.viz as any).vectormaputils.parse(`${urlConfigs.map}/${self.itemConfig.source()}`, { precision: 2 }, function (data) {
                                resolve(data);
                                if (self.itemConfig.source() === MapDataSourceEnum.USACities) {
                                    self.result.attributeNameItems(['shapeName']);
                                    self.result.fieldLabel("City");
                                }
                                else if (self.itemConfig.source() === MapDataSourceEnum.IrelandCounties) {
                                    self.result.attributeNameItems(['NAME_TAG']);
                                    self.result.fieldLabel("County");
                                }
                            });
                        }
                }
            });
        }

        let dataSource = await get() as any;
        if (this.isCustomMap(this.itemConfig.source()) && dataSource?.features && dataSource.features.length > 0) {
            if (dataSource.features[0].properties) {
                let attributes = [];
                for (var prop in dataSource.features[0].properties) {
                    attributes.push(prop);
                }
                self.result.attributeNameItems(attributes);
            }
        }

        if (self.result.attributeNameItems().indexOf(this.itemConfig.attributeName()) === -1) {
            this.itemConfig.attributeName(self.result.attributeNameItems()[0]);
        }

        return dataSource;
    }

    private getPaletteColorGroups() {
        let palette = [this.itemConfig.startColor(), this.itemConfig.endColor()];
        let colorGroups = this.result ? [this.result.minValue, this.result.maxValue] : [0, 0];
        if (this.result) {
            if (this.itemConfig.colorization() === MapColorizationEnum.Range && this.itemConfig.formattingRule()) {
                let formattingRuleObj = FormattingRuleService.getFormattingRuleById(this.itemConfig.formattingRule())
                let frColorGroups = formattingRuleObj.scales.map((x) => x.minValue);
                if (frColorGroups.length > 0) {
                    colorGroups = frColorGroups;
                    colorGroups[0] = this.result.minValue;
                    colorGroups.push(this.result.maxValue);
                }

                let colors = formattingRuleObj.scales.map((x) => x.color);
                if (colors.length > 0) {
                    if (!colors[0]) {
                        colors = formattingRuleObj.scales.map((x) => x.backgroundColor);
                    }

                    palette = colors;
                }

                this.result.legendLength = palette.length - 1;
            }
            else if (this.itemConfig.colorization() === MapColorizationEnum.Gradient) {
                if (this.itemConfig.gradientRanges() - 1 > 0) {
                    let dif = this.result.maxValue - this.result.minValue;
                    let stepValue = dif / (this.itemConfig.gradientRanges() - 1);
                    colorGroups = [this.result.minValue];
                    for (var i = 0; i < this.itemConfig.gradientRanges() - 1; i++) {
                        colorGroups.push(colorGroups[i] + stepValue);
                    }
                    colorGroups.push(this.result.maxValue);
                }

                this.result.legendLength = colorGroups.length - 2;
            }
        }
        return {
            palette: palette,
            colorGroups: colorGroups
        }
    }

    private formatValue(value: number, isLegend: boolean = false) {
        let dx = DevExpress as any;
        let largerNumber = "Normal";
        if (isLegend) {
            largerNumber = labelFormatEnumToString(this.itemConfig.legend.unitFormat());
        }

        const currencySign = this.result && this.result.metric.accountingStyle ? "accounting" : "standard";
        let decimalPlaces = this.result && (this.itemConfig.legend.decimalPlaces() === -1 || !isLegend) ? this.result.metric.decimalPlaces : this.itemConfig.legend.decimalPlaces();
        let format = this.result && (this.itemConfig.legend.format() === FieldFormatEnum.None || !isLegend) ? this.result.metric.metricFormat : this.itemConfig.legend.format();
        if (format == null)
            format = this.itemConfig.legend.format();

        if (decimalPlaces == null || decimalPlaces === -1)
            decimalPlaces = this.itemConfig.legend.decimalPlaces() === -1 ? 0 : this.itemConfig.legend.decimalPlaces();

        if (format === FieldFormatEnum.Currency)
            return `${dx.formatHelper.format(value, { style: "currency", currency: this.result ? this.result.currencyCode : "", currencyDisplay: "narrowSymbol", precision: decimalPlaces, currencySign: currencySign, largerNumber: largerNumber })}`;
        else if (format === FieldFormatEnum.Percentage)
            return `${dx.formatHelper.format(value, { style: "percent", precision: decimalPlaces, currencySign: currencySign })}`;

        return `${dx.formatHelper.format(value, { style: "decimal", precision: decimalPlaces, largerNumber: largerNumber, currencySign: currencySign })}`;
    }

    private getDataSource(): any {
        if (!this.result || !this.result.dataTable) {
            return {};
        }

        let newResult = {} as any;

        for (const data of this.result.dataTable) {
            let obj = {} as any;

            let value = data[this.result.field.index];
            if (value !== undefined && value !== null) {
                obj[this.result.field.queryName] = value;
                obj[this.result.metric.queryName] = data[this.result.metric.index];
                obj[this.notFoundColumn] = true;

                if (typeof value === "string") {
                    newResult[(value as string).toLowerCase().trimEnd().replace(/\0/g, '')] = obj;
                }
                else {
                    newResult[value] = obj;
                }
            }
        }

        return newResult;
    }
}